import React from 'react';
import { Share, ChevronDown, Activity, Calendar, Clock, Map, BarChart2, Layers, Info, ExternalLink } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
interface SummaryCard {
  title: string;
  value: string;
  unit: string;
  changes?: {
    label: string;
    value: string;
    isNegative: boolean;
  }[];
  subtitle?: string;
  icon?: React.ReactNode;
}
interface ChartData {
  month: string;
  val2024: number | null;
  val2025: number | null;
  val2026: number | null;
}
interface Filter {
  icon: React.ReactNode;
  label: string;
}
interface MarketDashboardProps {
  label: string;
  title: string;
  subtitle: string;
  summaryCards?: SummaryCard[];
  chartTitle: string;
  currentPrice: string;
  priceChange: {
    value: string;
    isNegative: boolean;
  };
  historicalChanges: {
    label: string;
    value: string;
    isNegative: boolean;
  }[];
  lastReportDate: string;
  filters: Filter[];
  chartData: ChartData[];
  yAxisDomain: [number, number];
  yAxisTicks: number[];
  currentPriceNum: number;
}
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-lg text-sm" data-id="element-97">
        <p className="font-semibold text-gray-900 mb-2" data-id="element-98">{label}</p>
        {payload.map((entry: any, index: number) => <div key={index} className="flex items-center gap-2 mb-1" data-id="element-99">
            <div className="w-2 h-2 rounded-full" style={{
          backgroundColor: entry.color
        }} data-id="element-100" />
            <span className="text-gray-600" data-id="element-101">{entry.name}:</span>
            <span className="font-medium text-gray-900" data-id="element-102">
              ${entry.value.toFixed(2)}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function MarketDashboard({
  label,
  title,
  subtitle,
  summaryCards,
  chartTitle,
  currentPrice,
  priceChange,
  historicalChanges,
  lastReportDate,
  filters,
  chartData,
  yAxisDomain,
  yAxisTicks,
  currentPriceNum
}: MarketDashboardProps) {
  return <div className="mb-12" data-id="element-103">
      {/* Summary Cards (Optional) */}
      {summaryCards && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-104">
          {summaryCards.map((card, idx) => <div key={idx} className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm" data-id="element-105">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3" data-id="element-106">
                {card.icon || <span className="text-gray-400" data-id="element-107">$</span>}
                {card.title}
              </div>
              <div className="flex items-baseline gap-1 mb-4" data-id="element-108">
                <span className="text-2xl font-bold text-gray-900" data-id="element-109">
                  {card.value}
                </span>
                <span className="text-xs text-gray-500" data-id="element-110">{card.unit}</span>
              </div>
              {card.changes ? <div className="grid grid-cols-3 gap-2 text-center" data-id="element-111">
                  {card.changes.map((change, cIdx) => <div key={cIdx} data-id="element-112">
                      <div className="text-[10px] text-gray-500 uppercase mb-1" data-id="element-113">
                        {change.label}
                      </div>
                      <div className={`text-xs font-medium ${change.isNegative ? 'text-red-600' : 'text-green-600'}`} data-id="element-114">
                        {change.value}
                      </div>
                    </div>)}
                </div> : card.subtitle ? <div className="text-xs text-gray-500 mt-6" data-id="element-115">
                  {card.subtitle}
                </div> : null}
            </div>)}
        </div>}

      {/* Chart Section */}
      <div className="bg-[#f8f9fa] rounded-2xl border border-gray-200 p-6 relative overflow-hidden" data-id="element-116">
        {/* Chart Header */}
        <div className="mb-6" data-id="element-117">
          <div className="flex items-center justify-between mb-2" data-id="element-118">
            <div className="flex items-center gap-2" data-id="element-119">
              <h3 className="text-xl font-bold text-gray-900" data-id="element-120">{chartTitle}</h3>
              <a href="https://meatmarket.ai/market-intelligence?utm_source=meat_poultry_magazine" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#002f5f] transition-colors" title="See More Details" data-id="element-121">
                <Info className="w-4 h-4" data-id="element-122" />
              </a>
            </div>
            <a href="https://meatmarket.ai/?utm_source=meat_poultry_magazine" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-[#002f5f] hover:underline flex items-center gap-1" data-id="element-123">
              View More Data
              <ExternalLink className="w-3 h-3" data-id="element-124" />
            </a>
          </div>
          <div className="flex items-baseline gap-3 mb-2" data-id="element-125">
            <span className="text-3xl font-bold text-gray-900" data-id="element-126">
              {currentPrice}
            </span>
            <span className={`text-sm font-medium flex items-center ${priceChange.isNegative ? 'text-red-600' : 'text-green-600'}`} data-id="element-127">
              {priceChange.isNegative ? '↓' : '↑'} {priceChange.value} Today
            </span>
            <div className="flex gap-2 text-xs font-medium ml-2" data-id="element-128">
              {historicalChanges.map((hc, idx) => <span key={idx} className={hc.isNegative ? 'text-red-600' : 'text-green-600'} data-id="element-129">
                  ({hc.label}: {hc.value})
                </span>)}
            </div>
          </div>
          <div className="text-xs text-gray-500" data-id="element-130">
            Last Report Date: {lastReportDate}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-8 relative z-10" data-id="element-131">
          {filters.map((filter, idx) => <button key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-md text-sm text-gray-700 hover:bg-gray-50 shadow-sm" data-id="element-132">
              <span className="text-gray-400" data-id="element-133">{filter.icon}</span>
              {filter.label}
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 ml-1" data-id="element-134" />
            </button>)}
        </div>

        {/* Chart Area */}
        <div className="h-[400px] w-full relative z-0" data-id="element-135">
          <ResponsiveContainer width="100%" height="100%" data-id="element-136">
            <AreaChart data={chartData} margin={{
            top: 20,
            right: 30,
            left: 0,
            bottom: 0
          }} data-id="element-137">
              <defs data-id="element-138">
                <linearGradient id="color2026" x1="0" y1="0" x2="0" y2="1" data-id="element-139">
                  <stop offset="5%" stopColor="#dc2626" stopOpacity={0.2} data-id="element-140" />
                  <stop offset="95%" stopColor="#dc2626" stopOpacity={0} data-id="element-141" />
                </linearGradient>
                <linearGradient id="color2025" x1="0" y1="0" x2="0" y2="1" data-id="element-142">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} data-id="element-143" />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-144" />
                </linearGradient>
                <linearGradient id="color2024" x1="0" y1="0" x2="0" y2="1" data-id="element-145">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2} data-id="element-146" />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} data-id="element-147" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" data-id="element-148" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
              fill: '#6b7280',
              fontSize: 12
            }} dy={10} data-id="element-149" />
              <YAxis domain={yAxisDomain} ticks={yAxisTicks} axisLine={false} tickLine={false} tick={{
              fill: '#6b7280',
              fontSize: 12
            }} dx={-10} tickFormatter={val => val.toFixed(2)} data-id="element-150" />
              <Tooltip content={<CustomTooltip data-id="element-152" />} data-id="element-151" />

              <ReferenceLine y={currentPriceNum} stroke="#9ca3af" strokeDasharray="3 3" data-id="element-153" />

              <Area type="monotone" dataKey="val2024" name="2024" stroke="#22c55e" strokeWidth={2} fillOpacity={1} fill="url(#color2024)" data-id="element-154" />
              <Area type="monotone" dataKey="val2025" name="2025" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#color2025)" data-id="element-155" />
              <Area type="monotone" dataKey="val2026" name="2026" stroke="#dc2626" strokeWidth={2} fillOpacity={1} fill="url(#color2026)" connectNulls={false} data-id="element-156" />
            </AreaChart>
          </ResponsiveContainer>

          {/* Watermark */}
          <div className="absolute bottom-4 right-4 flex items-center gap-1.5 opacity-50 pointer-events-none" data-id="element-157">
            <div className="bg-[#0d6b58] text-white text-[10px] font-bold px-1 rounded" data-id="element-158">
              MM
            </div>
            <span className="text-[#0d6b58] font-bold text-lg" data-id="element-159">
              MeatMarket.AI
            </span>
          </div>
        </div>
      </div>
    </div>;
}